/**
 * The closed permission catalog — every business operation a role may be
 * granted. A permission string names a business operation, never a
 * database or storage primitive; action tokens are never read, write,
 * create, update, delete, list, get, set.
 *
 * A role gates operations needing authority beyond membership. Membership
 * alone gates the member-facing view surfaces.
 *
 * Adding a permission:
 *  1. Append to the correct domain group below.
 *  2. Add its `hasPermission(...)` rule to the owning entity.
 *  3. Add its `PERMISSION_META` entry (the `satisfies` guard fails typecheck otherwise).
 *  4. Assign it to the role docs the provisioning script writes.
 *  5. Run `pnpm ican:rules` to regenerate firestore.rules.
 *
 * The catalog stays closed at fourteen operations — fourteen strings. A
 * permission enters only as a named
 * amendment filed against a real operational or regulatory trigger — the
 * credential surface earned the ninth (`workspace.credentials.manage`), the
 * door's undo-admission authority earned the tenth (`tickets.checkin.undo`):
 * reversing an admission is authority beyond ordinary scanning — the owner,
 * or an explicitly granted supervisor tier — since a phone holding
 * `tickets.checkin.scan` admits all evening and must not also be able to
 * erase what it recorded. It rides its own token rather than the unrelated
 * `tickets.reports.view` (a
 * read-only reporting grant that would silently also hand out live
 * door-write power to anyone holding it for sales visibility alone); and the
 * order-detail surface's buyer-PII correction earned the eleventh
 * (`tickets.orders.amend`): fixing a buyer's email address mutates the order
 * doc, it does not merely read it, so it rode `tickets.reports.view` only as
 * a stopgap — a reports-viewer reading the sales dashboard has no business
 * rewriting a buyer's contact details, and the resend action (re-sending
 * confirmation content that already exists, leaking nothing new) stays on
 * `tickets.reports.view` unchanged. The order purge earned the
 * twelfth (`tickets.orders.purge`): destroying a record and everything it
 * minted is a strictly greater authority than reversing its payment, which
 * `tickets.orders.refund` names and which leaves the record standing. And
 * the voucher catalogue earned the thirteenth (`tickets.vouchers.manage`):
 * minting a code gives money away BEFORE a sale and reveals ticket types the
 * public shop hides, which neither `tickets.orders.refund` (money returned
 * after a sale, against an order that already exists) nor
 * `tickets.events.manage` (a member who prices a ticket type does not
 * thereby get to mint free money) names. And paid promotion earned the
 * fourteenth (`tickets.ads.manage`): committing a lifetime budget at an ad
 * network, and stopping that spend, moves the workspace's OWN money to an
 * outside party — which `tickets.events.manage` never does (pricing a ticket
 * type asks a buyer for money, it does not spend any), which
 * `workspace.credentials.manage` does not reach (binding the ad account's
 * credential is not authority to spend against it, exactly as binding the
 * payment account is not authority to refund), and which
 * `tickets.reports.view` only reads after the fact. One coarse token for the
 * whole surface — read the campaigns, write the brief, edit the parked
 * proposal, launch it, stop it — because a member who may commit the budget
 * can already read what it bought.
 *
 * The door refuses a redeem only past the ticket's own frozen
 * `entryWindow.closesAt` — the last possible check-in time. There is no
 * early bound at the door, so no authority to waive one belongs in this
 * catalog.
 */
import { z } from "zod";

// Organization domains require dedicated authority: routing and external hostname ownership
// must never be acquired through an event-only administration grant. Owner provisioning includes it.
export const PERMISSIONS = [
	"workspace.domains.manage",
	// ── Events ────────────────────────────────────────────────────────
	// Also the R1 gate on workspace-card and settings edits; the credentials
	// surface carries its own token below (workspace.credentials.manage).
	"tickets.events.manage", // create/edit/archive events, ticket types, locations

	// ── Orders ────────────────────────────────────────────────────────
	// 'tickets.orders.read' intentionally absent — reads of buyer-identifying
	// order data gate on tickets.reports.view + mfa (order.entity.ts's own
	// read/list rule), never bare membership.
	"tickets.orders.refund", // initiate a refund batch against paid orders
	"tickets.orders.amend", // amend a placed order's buyer-facing fields (email today; address/name later)
	// 'tickets.orders.purge' — erase a test or complimentary order and all it minted
	// (tickets, orderAccess, delivery markers, Storage artifacts), returning
	// the quota and the voucher use it consumed. Its own token because
	// destroying a record is an authority no existing one names: refunding
	// reverses a payment and leaves the record standing, which is strictly
	// lesser than removing the record itself, and the catalog is explicitly
	// closed — each addition a named amendment.
	"tickets.orders.purge",

	// ── Guests ────────────────────────────────────────────────────────
	"tickets.guests.issue", // issue a complimentary guest ticket (checkouts/guest__{id})

	// ── Check-in ──────────────────────────────────────────────────────
	"tickets.checkin.scan", // query tickets by qrToken and redeem valid → redeemed
	"tickets.checkin.undo", // supervisor tier: reverse any admission, whichever door recorded it

	// ── Reporting ─────────────────────────────────────────────────────
	"tickets.reports.view", // read the rollups members see: orders, tickets, quota, refunds, storefront traffic

	// ── Members ───────────────────────────────────────────────────────
	// 'tickets.members.view' — read the member list: who holds which role, over
	// which events. A held grant rather than bare membership, because the member doc
	// carries colleague names and email addresses — personal data every member
	// should not see by default. The ROLE catalog stays on bare membership: four
	// fixed, non-confidential docs disclose nothing.
	"tickets.members.view",
	// 'tickets.members.invite' — invite a member, revoke a pending invite,
	// assign/remove a member's role. Member read/list widens to an OR against
	// this token: invite authority implies member-list visibility, so a custom
	// role granted invite-without-view cannot silently break the member list.
	"tickets.members.invite",

	// ── Roles ─────────────────────────────────────────────────────────
	// Grouped under the 'members' PICKER domain below — momo-starter's own
	// single-'workspaces'-section shape — so PermissionDomain gains no new
	// enum value.
	// 'tickets.roles.manage' — create, edit, delete custom roles and their
	// permission sets, through the createRole/editRole/deleteRole callables.
	// Each enforces target-power containment: a role's permission set must be
	// ⊆ the caller's own role's permissions (permissionsContain), so a holder
	// cannot mint or assign a role that reaches beyond their own authority.
	"tickets.roles.manage",

	// ── Credentials ───────────────────────────────────────────────────
	// Grouped under the 'events' PICKER domain below — the same overloaded
	// workspace-settings gate 'tickets.events.manage' once carried alone —
	// so PermissionDomain gains no new enum value.
	// One owner-tier token for every operating credential, deliberately not
	// split per provider: whoever holds the email transport holds OTP
	// delivery, hence sign-in, so "just the mailer" is never the lesser
	// grant it reads as.
	"workspace.credentials.manage", // bind the operating credentials — the payment account that moves the money, the identity the deployment sends as, and the keys that sign its wallet passes

	// ── Vouchers ──────────────────────────────────────────────────────
	// Grouped under the 'orders' PICKER domain below — the money the code
	// moves is order money — so PermissionDomain gains no new enum value. The
	// cost is named rather than hidden: a role author hunting for vouchers
	// under Events finds nothing there.
	// 'tickets.vouchers.manage' — mint, edit and delete the codes under an
	// event, read the catalogue the operator screens list, and read each
	// code's own redemption record. One token for the whole surface rather
	// than a read/write split: a member who can see how many redemptions a
	// code has left can already mint a code of their own. It is the ONLY
	// door — no buyer-facing rule exists at any tier, so an anonymous or
	// ordinary signed-in client falls to the default-deny tail. Operator-named
	// codes are short and guessable, and a publicly readable document would be
	// a free enumeration surface that also discloses each code's remaining
	// redemptions; the buyer never needs one, since every resolution happens
	// server-side against the Admin SDK.
	"tickets.vouchers.manage",

	// ── Advertising ───────────────────────────────────────────────────
	// Grouped under the 'events' PICKER domain below — promoting an event
	// is done from inside that event's own console — so PermissionDomain
	// gains no new enum value.
	// 'tickets.ads.manage' — the whole paid-promotion surface on an event:
	// read its campaigns (per event, and across every event at workspace
	// scope), write the brief, edit the parked proposal and its budget,
	// commit the money, and stop the spend. Denied until `mfa: true`, like
	// every other operator authority that moves money.
	"tickets.ads.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const PermissionSchema = z
	.enum(PERMISSIONS)
	.describe(
		"a business operation a role may perform — the closed catalog; never a database primitive",
	);

// ── Picker metadata ─────────────────────────────────────────────────────────
// The role admin surface renders a permission picker: each token carries a
// label, a one-line description, and a domain group, guarded by a
// compile-time `satisfies` exhaustiveness check — a token added to
// PERMISSIONS with no matching PERMISSION_META entry fails typecheck.
export const PermissionDomain = z.enum([
	"events",
	"orders",
	"guests",
	"checkin",
	"reports",
	"members",
]);

export const PermissionMetaSchema = z
	.object({
		// Short label for the picker chip or role-editor row.
		label: z.string().min(1),
		// One-line summary of what the permission authorises.
		description: z.string().min(1),
		// Picker group for section headers in the role editor.
		domain: PermissionDomain,
	})
	.strict();

export const PERMISSION_META = {
	"workspace.domains.manage": {
		label: "Manage domains",
		description: "Connect and disconnect organization domains.",
		domain: "events",
	},
	"tickets.events.manage": {
		label: "Manage events",
		description:
			"Create, edit, and archive events, ticket types, and locations; also edit the workspace’s branding and settings.",
		domain: "events",
	},
	"tickets.orders.refund": {
		label: "Refund orders",
		description:
			"Refund paid orders — a single ticket, a whole order, or many at once.",
		domain: "orders",
	},
	"tickets.orders.amend": {
		label: "Amend orders",
		description:
			"Correct a placed order's buyer-facing fields — email today, address and name later.",
		domain: "orders",
	},
	"tickets.orders.purge": {
		label: "Purge orders",
		description:
			"Erase a sandbox test order, a complimentary guest order, or an order that cost its buyer nothing, and everything it minted, returning the capacity and voucher use it consumed. A paid live order is out of reach.",
		domain: "orders",
	},
	"tickets.guests.issue": {
		label: "Issue guest tickets",
		description: "Issue free guest tickets.",
		domain: "guests",
	},
	"tickets.checkin.scan": {
		label: "Scan tickets",
		description: "Look up scanned tickets at the door and redeem them.",
		domain: "checkin",
	},
	"tickets.checkin.undo": {
		label: "Undo admissions",
		description:
			"Reverse an admission at the door, whichever account made it — supervisor authority beyond ordinary scanning.",
		domain: "checkin",
	},
	"tickets.reports.view": {
		label: "View reports",
		description:
			"See sales data: orders, tickets, remaining capacity, refunds, and storefront traffic analytics.",
		domain: "reports",
	},
	"tickets.members.view": {
		label: "View members",
		description:
			"See the member list — each person’s name, email, role, and which events they cover.",
		domain: "members",
	},
	"tickets.members.invite": {
		label: "Invite members",
		description:
			"Invite a member, revoke a pending invite, and assign or remove a member’s role.",
		domain: "members",
	},
	"tickets.roles.manage": {
		label: "Manage roles",
		description:
			"Create, edit, and delete custom roles and their permission sets.",
		domain: "members",
	},
	"workspace.credentials.manage": {
		label: "Manage integrations",
		description:
			"Set up the outside services the workspace runs on — the payment account that takes the money, the address it sends email from, and the keys or certificates behind its wallet passes and AI generation.",
		domain: "events",
	},
	"tickets.vouchers.manage": {
		label: "Manage vouchers",
		description:
			"Create, edit, and delete discount codes on an event, and see who has redeemed each one.",
		domain: "orders",
	},
	"tickets.ads.manage": {
		label: "Manage advertising",
		description:
			"Set up paid advertising for an event — write the brief, adjust what the ad says and who it reaches, commit the budget, and stop the spend.",
		domain: "events",
	},
} satisfies Record<Permission, z.infer<typeof PermissionMetaSchema>>;

// Every token is grantable — this deployment issues no service-account bypass
// token, so the catalog and the picker cover the same set and no
// `GrantablePermission` exclusion type is needed.

// ── Target-power containment ─────────────────────────────────────────────────
// permissionsContain — the one ⊆ comparison every server-side target-power
// containment site runs (assignGrant, editRole, deleteRole, removeGrant,
// revokeInvite): no callable may create, overwrite, mutate, delete, or revoke a
// target whose CURRENT effective power reaches beyond the caller's own role's
// permissions. Lives here beside the catalog because it is permission-model
// logic, not a firebase primitive — the five callables import it from @momo/types.
// A plain subset check: this catalog carries no wildcard token, so the seeded
// `owner` role reaches every target only because its permissions array already
// lists the whole catalog, never because `*` expands to it.
export function permissionsContain(
	callerPermissions: readonly string[],
	targetPermissions: readonly string[],
): boolean {
	return targetPermissions.every((permission) =>
		callerPermissions.includes(permission),
	);
}
